/**
 * Async thunks for the unified /api/notes endpoint. Every notes surface
 * goes through these — there are no per-surface notes actions anywhere
 * else (the old getComments / postComment / fetchTaskComments / etc. were
 * deleted as part of TREN-273).
 */

import queryString from 'query-string'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import {
  anchorKey,
  appendNote,
  removeNote,
  replaceNote,
  setForCall,
  setLoading,
  setNotes,
} from './notes.redux'

const notesUrl = (organizationId, query = {}) => {
  const params = queryString.stringify({ organization_id: organizationId, ...query })
  return `${apiService.workflow}/api/notes?${params}`
}

const noteResourceUrl = (noteId, organizationId) => {
  const params = queryString.stringify({ organization_id: organizationId })
  return `${apiService.workflow}/api/notes/${noteId}?${params}`
}

const buildCreateBody = ({
  noteType,
  id,
  userId,
  noteText,
  visibility = 'public',
  mentionedUserIds = [],
  callMomentTimestamp,
  criteriaScoreId,
}) => {
  const body = {
    note_type: noteType,
    id,
    user_id: userId,
    note_text: noteText,
    visibility,
    mentioned_user_ids: mentionedUserIds,
  }
  if (callMomentTimestamp != null) {
    body.call_moment_timestamp = callMomentTimestamp
  }
  if (criteriaScoreId != null) {
    body.criteria_score_id = criteriaScoreId
  }
  return body
}

/**
 * Fetch all notes for one anchor (e.g., one scorecard, one task, one call).
 * Use `fetchAllNotesForCall` when you want the union of every note type
 * touching a call.
 */
export const fetchNotes =
  ({ noteType, id, organizationId }) =>
  async (dispatch) => {
    const key = anchorKey(noteType, id)
    dispatch(setLoading({ [key]: true }))
    try {
      const url = notesUrl(organizationId, { note_type: noteType, id })
      const notes = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setNotes({ noteType, id, notes }))
      return notes
    } catch (err) {
      toast.error('Failed to load notes', { cause: err })
      return []
    } finally {
      dispatch(setLoading({ [key]: false }))
    }
  }

/**
 * Aggregate read across every note type touching a call. Used by the call
 * detail Comments tab to show everything in one place.
 */
export const fetchAllNotesForCall =
  ({ callId, organizationId }) =>
  async (dispatch) => {
    const key = `for-call:${callId}`
    dispatch(setLoading({ [key]: true }))
    try {
      const params = queryString.stringify({ organization_id: organizationId })
      const url = `${apiService.workflow}/api/notes/for-call/${callId}?${params}`
      const notes = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setForCall({ callId, notes }))
      return notes
    } catch (err) {
      toast.error('Failed to load notes', { cause: err })
      return []
    } finally {
      dispatch(setLoading({ [key]: false }))
    }
  }

export const createNote =
  ({
    noteType,
    id,
    noteText,
    visibility = 'public',
    mentionedUserIds = [],
    callMomentTimestamp,
    criteriaScoreId,
    organizationId,
  }) =>
  async (dispatch, getState) => {
    const { user_id: userId } = getState().currentUser
    const body = buildCreateBody({
      noteType,
      id,
      userId,
      noteText: noteText.trim(),
      visibility,
      mentionedUserIds,
      callMomentTimestamp,
      criteriaScoreId,
    })
    try {
      const note = await fetchingAPI(
        notesUrl(organizationId),
        'POST',
        dispatch,
        JSON.stringify(body)
      )
      dispatch(appendNote({ noteType, id, note }))
      toast.success('Note added')
      return note
    } catch (err) {
      toast.error('Failed to save note', { cause: err })
      return null
    }
  }

/**
 * Partial update. `fields` matches `NoteUpdateRequest` on the backend
 * (note_text, visibility, mentioned_user_ids, call_moment_timestamp,
 * deleted). `anchor` is needed so we can route the response back to the
 * right slice of state.
 */
export const patchNote =
  ({ noteId, fields, anchor, organizationId }) =>
  async (dispatch) => {
    try {
      const note = await fetchingAPI(
        noteResourceUrl(noteId, organizationId),
        'PATCH',
        dispatch,
        JSON.stringify(fields)
      )
      dispatch(replaceNote({ noteType: anchor.noteType, id: anchor.id, note }))
      return note
    } catch (err) {
      toast.error('Failed to update note', { cause: err })
      return null
    }
  }

export const deleteNote =
  ({ noteId, anchor, organizationId }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(noteResourceUrl(noteId, organizationId), 'DELETE', dispatch)
      dispatch(removeNote({ noteType: anchor.noteType, id: anchor.id, noteId }))
      return true
    } catch (err) {
      toast.error('Failed to delete note', { cause: err })
      return false
    }
  }
